import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  DIR_ORDER,
  DIR_REGISTRY,
  EXTRA_NAV,
} from '@/pages/directories/model/registry';
import { DirStyles } from '@/pages/directories/ui/refTheme';

import { activeNavItemStyle } from './DesktopSidebar.styles';
import { getNavItemClass, SidebarItemContent } from './DesktopSidebarNav';
import type { SidebarLinkItem } from './DesktopSidebar.types';

const POPOVER_W = 244;
const POPOVER_H = 530;

/** Пункт навбара «Справочники»: по клику открывает поповер у курсора со списком
 *  справочников (не переход по ссылке). Активен на любом /directories/*. */
export function DirectoriesNavItem({ item }: { item: SidebarLinkItem }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const activeSlug = location.pathname.startsWith('/directories/')
    ? location.pathname.split('/')[2]
    : null;
  const isActive = location.pathname.startsWith('/directories');

  useEffect(() => {
    if (!pos) {
      return;
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPos(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pos]);

  const open = (e: { clientX: number; clientY: number }) => {
    const pad = 8;
    let x = e.clientX;
    let y = e.clientY;
    if (x + POPOVER_W > window.innerWidth - pad) {
      x = window.innerWidth - POPOVER_W - pad;
    }
    if (y + POPOVER_H > window.innerHeight - pad) {
      y = window.innerHeight - POPOVER_H - pad;
    }
    setPos({ x: Math.max(pad, x), y: Math.max(pad, y) });
  };

  const go = (slug: string) => {
    setPos(null);
    navigate(`/directories/${slug}`);
  };

  return (
    <>
      <button
        type="button"
        className={getNavItemClass(isActive)}
        style={isActive ? activeNavItemStyle : undefined}
        onClick={open}
      >
        <SidebarItemContent
          Icon={item.icon}
          label={item.label}
          active={isActive}
        />
      </button>

      {pos &&
        createPortal(
          <div className="dir-scope">
            <DirStyles />
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 999 }}
              onMouseDown={() => setPos(null)}
            />
            <div
              className="dir-navpop animate-in fade-in-0 zoom-in-95 duration-150"
              style={{
                position: 'fixed',
                left: pos.x,
                top: pos.y,
                zIndex: 1000,
              }}
            >
              {DIR_ORDER.map((slug) => {
                const cfg = DIR_REGISTRY[slug];
                const { Icon } = cfg;
                const active = activeSlug === slug;
                return (
                  <div
                    key={slug}
                    className={`dir-navpop-item${active ? ' dir-navpop-item--active' : ''}`}
                    onClick={() => go(slug)}
                  >
                    <span className="dir-navpop-badge">
                      <Icon size={15} strokeWidth={2} />
                    </span>
                    {cfg.title}
                  </div>
                );
              })}

              <div className="dir-rowmenu-sep" />

              {EXTRA_NAV.map(({ slug, title, Icon }) => {
                const active = activeSlug === slug;
                return (
                  <div
                    key={slug}
                    className={`dir-navpop-item${active ? ' dir-navpop-item--active' : ''}`}
                    onClick={() => go(slug)}
                  >
                    <span className="dir-navpop-badge">
                      <Icon size={15} strokeWidth={2} />
                    </span>
                    {title}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
